import { courseQuestionnaireScores } from '@/lib/courseQuestionnaireScores';
import {
  definedPointTotal,
  replacementTotalScore,
  type FormsAnswerQuestion,
} from '@/lib/formsAnswerValue';
import { resolveAssignmentScores, type ScoringConfig } from '@/lib/formsScoring';
import { computeSeverity, type Severity } from '@/lib/severityUtils';
import { courseQuestionnairePrimaryScore } from '@/lib/stepSendQuestionnaires';

export type ScoreDisplay =
  | { kind: 'none' }
  | { kind: 'total'; value: number; max: number | null; severity: Severity | null }
  | { kind: 'physical_mental'; physical: number; mental: number };

export type SeverityBand = {
  severity: Severity;
  min: number;
  max: number;
};

const PHQ9_BANDS: SeverityBand[] = [
  { severity: 'minimal', min: 0, max: 4 },
  { severity: 'mild', min: 5, max: 9 },
  { severity: 'moderate', min: 10, max: 14 },
  { severity: 'moderately_severe', min: 15, max: 19 },
  { severity: 'severe', min: 20, max: 27 },
];

const GAD7_BANDS: SeverityBand[] = [
  { severity: 'minimal', min: 0, max: 4 },
  { severity: 'mild', min: 5, max: 9 },
  { severity: 'moderate', min: 10, max: 14 },
  { severity: 'severe', min: 15, max: 21 },
];

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function templateKind(name: string | null | undefined): 'phq9' | 'gad7' | 'sf36' | 'other' {
  const upper = (name ?? '').toUpperCase();
  if (upper.includes('PHQ-9') || upper.includes('PHQ9')) return 'phq9';
  if (upper.includes('GAD-7') || upper.includes('GAD7')) return 'gad7';
  if (upper.includes('SF-36') || upper.includes('SF36')) return 'sf36';
  return 'other';
}

export function questionnaireScoreMax(name: string | null | undefined): number | null {
  const kind = templateKind(name);
  switch (kind) {
    case 'phq9':
      return 27;
    case 'gad7':
      return 21;
    case 'sf36':
      return 100;
    case 'other':
      return null;
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

export function questionnaireSeverityBands(name: string | null | undefined): SeverityBand[] | null {
  const kind = templateKind(name);
  switch (kind) {
    case 'phq9':
      return PHQ9_BANDS;
    case 'gad7':
      return GAD7_BANDS;
    case 'sf36':
    case 'other':
      return null;
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
}

export function assignmentScoreDisplay(
  score: Record<string, unknown> | null | undefined,
  templateName: string | null | undefined,
): ScoreDisplay {
  if (!score) return { kind: 'none' };
  const kind = templateKind(templateName);
  if (kind === 'sf36') {
    const rows = courseQuestionnaireScores(score);
    const physical = rows.find((row) => /physical|fysisk/i.test(row.label));
    const mental = rows.find((row) => /mental|psykisk/i.test(row.label));
    if (physical && mental) {
      return { kind: 'physical_mental', physical: physical.value, mental: mental.value };
    }
  }
  const value =
    courseQuestionnairePrimaryScore(score) ??
    asNumber(score.total) ??
    asNumber(score.score) ??
    asNumber(score.value);
  if (value == null) return { kind: 'none' };
  const max =
    asNumber(score.max_score) ??
    asNumber(score.maxScore) ??
    asNumber(score.maximum) ??
    asNumber(score.max) ??
    questionnaireScoreMax(templateName);
  const severity = computeSeverity(value, templateName);
  return { kind: 'total', value, max, severity };
}

export type ShownScoreQuestion = FormsAnswerQuestion & {
  id: string;
  text?: string;
  category_id?: string;
  scoring?: { method?: string; weight?: number; max_score?: number };
};

function asScoreRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/** True when the saved total is 0, so topic-row answers may still carry points. */
export function assignmentStoredTotalIsZero(
  score: unknown,
  templateName: string | null | undefined,
): boolean {
  const display = assignmentScoreDisplay(asScoreRecord(score), templateName);
  return display.kind === 'total' && display.value === 0;
}

/**
 * The total shown on the opened questionnaire.
 * A stored 0 with no domain scores is replaced by the sum of option points,
 * including a level+note object. A non-zero stored total, an SF-36 pair,
 * and a real 0 are left as saved.
 */
export function shownQuestionnaireScore(input: {
  storedScore: unknown;
  templateName?: string | null;
  questions?: readonly ShownScoreQuestion[] | null;
  responses?: Record<string, unknown> | null;
  scoringConfig?: ScoringConfig | null;
}): ScoreDisplay {
  const storedScore = asScoreRecord(input.storedScore);
  const stored = assignmentScoreDisplay(storedScore, input.templateName);
  if (!input.questions || !input.responses) return stored;

  const questions = input.questions.filter((question) => question.id.length > 0);
  const scoreResult = resolveAssignmentScores(
    questions.map((question) => ({
      id: question.id,
      text: question.text ?? '',
      type: question.type,
      category_id: question.category_id,
      options: question.options,
      scoring: question.scoring,
    })),
    input.responses,
    input.scoringConfig,
    storedScore,
  );
  const pointTotal = definedPointTotal(questions, input.responses);
  const replacement = replacementTotalScore({
    kind: stored.kind,
    storedValue: stored.kind === 'total' ? stored.value : null,
    pointTotal,
    domainTotal: scoreResult && scoreResult.domainScores.length > 0 ? scoreResult.totalScore : null,
    hasDomainScores: Boolean(scoreResult?.domainScores.length),
  });
  if (replacement == null) return stored;
  return {
    kind: 'total',
    value: replacement,
    max: stored.kind === 'total' ? stored.max : null,
    severity: null,
  };
}

export type AnswerScoreDetail = {
  score_details?: unknown;
  responses?: Record<string, unknown> | null;
  questionnaire?: {
    questions?: readonly ShownScoreQuestion[] | null;
    scoring_config?: ScoringConfig | null;
  } | null;
};

/**
 * Udfyldt list total. Same function as the opened questionnaire once the
 * answers are loaded. A stored 0 stays hidden while that load is in flight.
 */
export function listQuestionnaireScore(input: {
  storedScore: unknown;
  templateName?: string | null;
  detail?: AnswerScoreDetail | null;
  detailPending?: boolean;
}): { display: ScoreDisplay; pending: boolean } {
  const needsAnswers = assignmentStoredTotalIsZero(input.storedScore, input.templateName);
  if (!needsAnswers) {
    return {
      pending: false,
      display: shownQuestionnaireScore({
        storedScore: input.storedScore,
        templateName: input.templateName,
      }),
    };
  }
  if (input.detailPending) {
    return { pending: true, display: { kind: 'none' } };
  }
  const detail = input.detail;
  return {
    pending: false,
    display: shownQuestionnaireScore({
      storedScore: detail?.score_details ?? input.storedScore,
      templateName: input.templateName,
      questions: detail?.questionnaire?.questions,
      responses: detail?.responses,
      scoringConfig: detail?.questionnaire?.scoring_config ?? null,
    }),
  };
}

/** History row score after the list has applied the shared total. */
export function historyScoreAfterShownTotal(
  storedScore: Record<string, unknown> | null,
  state: { display: ScoreDisplay; pending: boolean },
  templateName: string | null | undefined,
): Record<string, unknown> | null {
  if (state.pending) return null;
  if (state.display.kind !== 'total') return storedScore;
  if (!assignmentStoredTotalIsZero(storedScore, templateName)) return storedScore;
  if (state.display.value === 0) return storedScore;
  return {
    ...(storedScore ?? {}),
    total_score: state.display.value,
    totalScore: state.display.value,
  };
}

export function scoreNumericValue(display: ScoreDisplay): number | null {
  switch (display.kind) {
    case 'none':
      return null;
    case 'total':
      return display.value;
    case 'physical_mental':
      return display.physical;
    default: {
      const _exhaustive: never = display;
      return _exhaustive;
    }
  }
}

export function sparklinePoints(
  values: number[],
  width = 64,
  height = 22,
): { points: string; dots: { x: number; y: number; last: boolean }[] } {
  if (values.length === 0) return { points: '', dots: [] };
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const span = hi - lo || 1;
  const n = values.length;
  const xs = values.map((_, i) => (n === 1 ? width / 2 : 3 + (i * (width - 6)) / (n - 1)));
  const ys = values.map((v) => height - 3 - ((v - lo) / span) * (height - 6));
  const dots = xs.map((x, i) => ({ x, y: ys[i] ?? 0, last: i === n - 1 }));
  return {
    points: xs.map((x, i) => `${x.toFixed(1)},${(ys[i] ?? 0).toFixed(1)}`).join(' '),
    dots,
  };
}
